export type StreamPagesSetting = 'off' | 'on';

export function streamPagesSetting(value: string | null | undefined): StreamPagesSetting {
  return value === 'on' ? 'on' : 'off';
}

function readParams(search: string): URLSearchParams {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  return new URLSearchParams(raw);
}

export function shouldRenderStreamPages(input: {
  setting?: string | null;
  search: string;
  previewKey: 'home' | 'view';
  viewer: { isOwner: boolean; isCommissioner: boolean };
}): boolean {
  const params = readParams(input.search);
  if (params.get('view') === 'classic') return false;
  if (streamPagesSetting(input.setting) === 'on') return true;
  const operator = input.viewer.isOwner || input.viewer.isCommissioner;
  return params.get(input.previewKey) === 'stream' && operator;
}

export function isMemberPreview(
  search: string,
  viewer: { isOwner: boolean; isCommissioner: boolean; isOperator?: boolean }
): boolean {
  const operator = viewer.isOwner || viewer.isCommissioner || Boolean(viewer.isOperator);
  return readParams(search).get('as') === 'member' && operator;
}

/** Sidebar, phone bar, and the role badge follow the preview, not the real role. */
export function shellUsesOperatorNav(isOperator: boolean, previewing: boolean): boolean {
  return isOperator && !previewing;
}

/**
 * Keep the stream preview on the next in-app link.
 * Operators who are not previewing still need view=stream while the setting is off.
 * A link back to Home uses home=stream instead of view=stream.
 */
export function carryStreamSearch(
  currentSearch: string,
  viewer: { operator: boolean },
  extras?: Record<string, string>,
  target: 'stream' | 'home' = 'stream'
): string {
  const current = readParams(currentSearch);
  const previewing = current.get('as') === 'member' && viewer.operator;
  const alreadyStreaming = current.get('view') === 'stream' || current.get('home') === 'stream';
  const next = new URLSearchParams();
  if (previewing) next.set('as', 'member');
  if (previewing || viewer.operator || alreadyStreaming) {
    next.set(target === 'home' ? 'home' : 'view', 'stream');
  }
  for (const [key, value] of Object.entries(extras ?? {})) {
    if (value) next.set(key, value);
  }
  const qs = next.toString();
  return qs ? `?${qs}` : '';
}

/** Append the preview query to a league path so the next click stays in the preview. */
export function withPreviewQuery(to: string, currentSearch: string): string {
  const path = to.split('?')[0];
  const home = /\/leagues\/[^/]+$/.test(path);
  const stream = path.includes('/matches') || path.includes('/archive');
  if (!home && !stream) return `${path}?as=member`;
  return `${path}${carryStreamSearch(currentSearch, { operator: true }, undefined, home ? 'home' : 'stream')}`;
}
